<template>
  <div>
    <UtdModalDialog ref="modalDialog"
                    :hide-header-close="false"
                    :close-on-overlay-click="false"
                    :close-on-escape="true"
                    @closed="handleClose">
      <template #header>Merge accounts</template>
      <div class="merge-accounts ds1-utd-js2">
        <div class="merge-accounts__container">
          <div class="merge-accounts__header">
            <h2 class="ds1-utd-h2">
              {{ $t('LOGIN.MERGE_EXISTING_ACCOUNT') }}
            </h2>
            <h3 class="ds1-utd-h4">
              {{ isSsoToSsoAutoMergeVisible 
                ? 'Find your existing account using the options below:' 
                : 'Find your existing account by entering your Username:' }}
            </h3>
          </div>
          <LoginMergeFormControls ref="loginFormControls"
                                  :merge-source="C_REG_MERGE.REGISTRATION_MERGE_TYPE"
                                  :show-searchable-institutions="isSsoToSsoAutoMergeVisible"
                                  @open-forgot-password="openForgotPassword"
                                  @find-account="processMerge"
                                  @fetched-autocomplete="processScroll"
                                  @navigate-to-sso="autoMergeSso" />
        </div>
      </div>
      <template #footer>
        <UtdButton id="cancelMergeBtn"
                   button-size="large"
                   button-style="ghost"
                   @click="cancelMerge">
          Cancel
        </UtdButton>
      </template>
    </UtdModalDialog>
    <ForgotPasswordModal ref="forgotPassword" @close-forgot-password-modal="openModal" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import {
    C_HTTP,
    C_REG_MERGE,
    isOidcRegex
} from '_acaSrc/utility/constants';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import ForgotPasswordModal from '_acaSrc/account/components/ForgotPasswordModal.vue';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import useMergeAccounts from '_acaSrc/composables/mergeAccounts.composable.js';
import { getWindow, getDocument } from '_acaSrc/utility/DOM';
import { setQueryParamValue } from '_acaSrc/utility/http';
import LoginMergeFormControls from '../login/LoginMergeFormControls.vue';
import { SET_OIDC_MERGE_STATUS } from '_acaSrc/store/login.store';

const store = useStore();

const showPasswordSection = ref(false);

const emit = defineEmits([ 'cancel-merge' ]);
const cancelMerge = () => {
    emit('cancel-merge');
};

const autoMergeSso = (ssoUrl) => {
    const { navigateToSso } = useMergeAccounts();
    navigateToSso(ssoUrl, C_REG_MERGE.REGISTRATION_MERGE_TYPE);
};

const modalDialog = ref(null);
const openModal = () => {
    modalDialog.value.open();
};

const handleClose = () => {
    resetMerge();
    showPasswordSection.value = false;
    store.commit(`login/${SET_OIDC_MERGE_STATUS}`, '');
};

const closeModal = () => {
    modalDialog.value.close();
};

defineExpose({
    openModal,
    closeModal
});

const forgotPassword = ref(null);
const openForgotPassword = () => {
    logUiClickEventHelper({
        uiElementName: 'regMerge_mergeForgotPasswordLink'
    });
    forgotPassword.value.open();
    closeModal();
};

const supportTag = computed(() => store.getters['footer/supportTag']);
const isSsoSupportTag = computed(() => isOidcRegex.test(supportTag.value.serverTag));

const navigateHome = (mergeType) => {
    const url = setQueryParamValue(
        C_HTTP.DEFAULT_HOMEPAGE_PATH, C_REG_MERGE.REG_MERGE_SUCCESS_PARAM, mergeType
    );
    closeModal();
    getWindow().location.href = url;
};

const isEmrAuthenticated = computed(() => store.getters['profile/isEmrAuthenticated']);
const determineMergeType = () => {
    const mergeType = isEmrAuthenticated.value || !isSsoSupportTag.value 
        ? C_REG_MERGE.MESSAGES.GENERIC_MERGE.name 
        : C_REG_MERGE.MESSAGES.SSO_MERGE.name;
    return mergeType;
};
const processMerge = async() => {
    const mergeType = determineMergeType();
    const mergeSuccess = await performAutoMerge();
    await nextTick();
    if (!mergeSuccess) {
        return;
    }
    navigateHome(mergeType);
};

const processScroll = () => {
    nextTick(() => {
        const el = getDocument().querySelector('.utd-dialog-content');
        el?.scrollTo({ behavior: 'smooth', top: el.scrollHeight });
    });
};

const {
    resetMerge,
    performAutoMerge
} = useMergeAccounts();

const isSsoToSsoAutoMergeVisible = computed(() => 
    store.getters['feature/isSsoToSsoAutoMergeEnabled']
);

</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles.less';

.merge-accounts {
  .ds1-ma-4();

  :deep(.notification-content) {
    .ds1-mb-0();
    .ds1-mt-1();
  }
}

.merge-accounts__header {
  .ds1-mb-2();
}

.utd-dialog-frame &.merge-accounts {
  max-width: 540px;
}

.merge-accounts__form-submit {
  display: flex;
}

#checkUsernameBtn, #mergeAccountsBtn {
  flex: 1;
}

.merge-accounts__password-section {
  .ds1-mt-4();

  :deep(.wk-field-password) {
    .ds1-mb-0();
  }
}

.merge-accounts__fieldset {
  .ds1-pa-0();
  border: none;

  .utd-text-input {
    .ds1-mb-0();
  }
}

.merge-accounts__input {
  .ds1-mb-3();
}

.merge-accounts__help {
  .ds1-mb-2();
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
}

.merge-accounts__help-link {
  .ds1-pa-0();
  white-space: nowrap;
}

#mergeErrorNotification {
  .ds1-mb-3();
}
</style>